import { useCallback, useEffect, useRef, useState } from 'react';

import { analyticsClient } from 'utils/analytics/client';

// Privacy extensions (Privacy Badger, uBlock Origin, etc.) block *.navattic.com
// (e.g. prismatic-io.navattic.com) as a third-party tracker, so the Navattic
// demo silently renders as blank space
// with no error we could observe from the iframe itself (it's cross-origin, so we
// can't inspect its contents). This probes reachability out-of-band with a no-cors
// fetch and only shows a fallback message when the probe fails/times out AND the
// iframe hasn't already fired its own `load` event (which always wins, to avoid
// false positives on slow connections). Fires a 'Navattic Fallback Shown' analytics
// event so we can tell how often visitors actually hit this. Safe to delete this
// file - and its two call sites in `index.tsx` - if/when Navattic moves to a
// first-party custom domain.

const PROBE_TIMEOUT_MS = 3500;
const PROBE_ASSET_PATH = '/favicon.ico';

export const useNavatticFallback = (src: Maybe<string>, enabled: boolean) => {
  const [showFallback, setShowFallback] = useState(false);
  const iframeLoadedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !src) {
      return undefined;
    }

    let probeUrl: string;
    try {
      probeUrl = new URL(PROBE_ASSET_PATH, src).toString();
    } catch {
      return undefined;
    }

    iframeLoadedRef.current = false;
    setShowFallback(false);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    fetch(probeUrl, { mode: 'no-cors', cache: 'no-store', signal: controller.signal })
      .catch(() => {
        if (!iframeLoadedRef.current) {
          setShowFallback(true);
          analyticsClient.track('Navattic Fallback Shown', { src });
        }
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [src, enabled]);

  const handleIframeLoad = useCallback(() => {
    iframeLoadedRef.current = true;
    setShowFallback(false);
  }, []);

  return { showFallback, handleIframeLoad };
};

export default useNavatticFallback;
